/**
 * Password Requirement Indicator
 *
 * Visual indicator for individual password strength requirements,
 * showing whether each requirement is satisfied.
 */

export interface PasswordRequirementProps {
  /** Whether the requirement is satisfied */
  ok: boolean;
  /** Text description of the requirement */
  text: string;
}

/**
 * Individual password requirement indicator with visual feedback
 *
 * Displays a circular indicator that changes color based on
 * whether the requirement is satisfied.
 *
 * @example
 * ```tsx
 * <PasswordRequirement ok={password.length >= 8} text="At least 8 characters" />
 * ```
 */
export function PasswordRequirement({ ok, text }: PasswordRequirementProps) {
  return (
    <div
      className="flex items-center gap-2 text-xs"
      data-testid={`pw-req-${text}`}
    >
      <span
        className={`inline-flex h-4 w-4 items-center justify-center rounded-full border transition-colors ${
          ok
            ? "border-emerald-400 bg-emerald-100"
            : "border-gray-300 bg-gray-100"
        }`}
        aria-hidden
      >
        <span
          className={`h-2 w-2 rounded-full ${ok ? "bg-emerald-500" : "bg-gray-300"}`}
        />
      </span>
      <span className={ok ? "text-emerald-700" : "text-gray-500"}>{text}</span>
    </div>
  );
}
